import { NextRouter } from "next/router";
import { useContext, useEffect, useRef } from "react";

import {
  BloomMilestone,
  UserResponse,
} from "@every.org/common/src/codecs/entities";
import { isValidUsername } from "@every.org/common/src/codecs/username";
import {
  AdminLevel,
  NonprofitAdminStatus,
} from "@every.org/common/src/entity/types";
import { getUserFullName } from "@every.org/common/src/helpers/username";

import { AuthContext } from "src/context/AuthContext";
import { NonprofitsContext } from "src/context/NonprofitsContext";
import { getNonprofitOrUndefined } from "src/context/NonprofitsContext/selectors";
import { UsersContext } from "src/context/UsersContext";
import { getUser, userOrUndefined } from "src/context/UsersContext/selectors";
import { getOrganizationContext } from "src/context/bloomOrganizationContext";

export const EMPTY_PROFILE_CONTEXT = {
  person: null,
  nonprofit: null,
  milestones: [] as BloomMilestone[],
};

const PERSON_ROUTES = new Set([
  "/user/[username]",
  "/user/[username]/donations",
  "/user/[username]/lists",
  "/user/[username]/likes",
  "/user/[username]/joins",
  "/user/[username]/fundraisers",
  "/user/[username]/followers",
  "/user/[username]/following",
]);

const NONPROFIT_ADMIN_PAGE =
  "/[nonprofitSlugOrUsername]/admin/[[...adminPath]]";

function personProfile(
  person: Pick<
    UserResponse,
    | "id"
    | "firstName"
    | "lastName"
    | "username"
    | "bioText"
    | "profileImageCloudinaryId"
    | "isPrivate"
  >,
  canEdit: boolean
) {
  return {
    id: person.id,
    profile: {
      canEdit,
      name: getUserFullName(person) ?? undefined,
      username: person.username,
      // Cached private biographies may belong to a previous account.
      bioText:
        canEdit || person.isPrivate === false ? person.bioText : undefined,
      hasProfileImage:
        person.profileImageCloudinaryId === undefined
          ? undefined
          : !!person.profileImageCloudinaryId,
      isPrivate: person.isPrivate,
    },
  };
}

/**
 * Keep the visitor's selected admin nonprofit while they browse. Only another
 * admin page changes that selection; the page still owns all data fetching.
 */
export function useBloomProfileContext({
  pathname,
  query = {},
}: Pick<NextRouter, "pathname" | "query">) {
  const { user } = useContext(AuthContext);
  const nonprofits = useContext(NonprofitsContext);
  const users = useContext(UsersContext);
  const selection = useRef<{
    userId?: string;
    nonprofitId?: string;
  }>({});
  const memberships =
    user?.adminLevelForDisplay !== AdminLevel.RESTRICTED
      ? user?.adminFor?.filter(
          ({ status }) => status === NonprofitAdminStatus.CONFIRMED
        ) ?? []
      : [];
  const slug = query.nonprofitSlugOrUsername;
  const pageNonprofit =
    pathname === NONPROFIT_ADMIN_PAGE && typeof slug === "string"
      ? getNonprofitOrUndefined(nonprofits, { slug })
      : undefined;
  const pageMembership =
    pathname === NONPROFIT_ADMIN_PAGE
      ? memberships.find(
          ({ nonprofit }) =>
            nonprofit.id === pageNonprofit?.id || nonprofit.primarySlug === slug
        )
      : undefined;
  const rememberedMembership =
    selection.current.userId === user?.id
      ? memberships.find(
          ({ nonprofit }) => nonprofit.id === selection.current.nonprofitId
        )
      : undefined;
  // A sole membership is unambiguous. With multiple memberships, wait for an
  // admin-page selection rather than choosing an arbitrary organization.
  const selectedMembership =
    pageMembership ??
    rememberedMembership ??
    (memberships.length === 1 ? memberships[0] : undefined);
  const nonprofitId = selectedMembership?.nonprofit.id;
  const userId = user?.id;
  useEffect(() => {
    selection.current = { userId, nonprofitId };
  }, [userId, nonprofitId]);
  const nonprofit = nonprofitId
    ? nonprofits.nonprofitsById.get(nonprofitId)
    : undefined;
  const organization = nonprofit
    ? getOrganizationContext(nonprofit)
    : undefined;

  const username = PERSON_ROUTES.has(pathname)
    ? query.username
    : pathname === "/[nonprofitSlugOrUsername]" &&
      typeof slug === "string" &&
      slug.startsWith("@")
    ? slug.slice(1)
    : undefined;
  const cached =
    typeof username === "string" && isValidUsername(username)
      ? userOrUndefined(getUser(users, { username }))
      : undefined;
  // Auth owns the freshest saved facts for the signed-in person's profile.
  const person =
    user &&
    (pathname === "/settings/profile" ||
      (typeof username === "string" && username === user.username) ||
      cached?.id === user.id)
      ? user
      : cached;
  const canEditPerson = !!user && person?.id === user.id;

  if (!user) {
    return EMPTY_PROFILE_CONTEXT;
  }
  return {
    person: person ? personProfile(person, canEditPerson) : null,
    nonprofit:
      nonprofit && organization
        ? {
            ...organization.nonprofit,
            profile: { canEdit: true, ...organization.facts },
            fundraising: nonprofit.fundraising,
            numDonateButtons: nonprofit.numDonateButtons,
            customizedDonateForm: nonprofit.customizedDonateForm,
            numAdmins: nonprofit.numAdmins,
            // Bloom's generic name for when an account started.
            createdAt: nonprofit.approvedAt?.toISOString(),
          }
        : null,
    milestones: organization?.milestones ?? [],
  };
}
